import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Download, ChevronRight } from "lucide-react";
import api from "../../lib/api";

const STATUS_COLORS = {
  placed: "bg-jow-navy/10 text-jow-navy",
  packed: "bg-jow-gold/20 text-jow-navy",
  shipped: "bg-blue-50 text-blue-700",
  on_rikshaw: "bg-jow-gold/30 text-jow-navy",
  out_for_delivery: "bg-jow-pink/20 text-jow-pink",
  delivered: "bg-green-50 text-green-700",
  cancelled: "bg-red-50 text-red-700",
  cancelled_after_hold: "bg-red-50 text-red-700",
  reserved: "bg-purple-50 text-purple-700",
  returned: "bg-orange-50 text-orange-700",
};

const PAY_COLORS = {
  pending: "bg-jow-gold/20 text-jow-navy",
  paid: "bg-green-50 text-green-700",
  failed: "bg-red-50 text-red-700",
  refunded: "bg-blue-50 text-blue-700",
};

export default function AdminOrders() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    q: searchParams.get("q") || "",
    status: "",
    payment_status: "",
  });

  const load = async () => {
    const params = new URLSearchParams({ page, limit: 20 });
    if (filters.q) params.set("q", filters.q);
    if (filters.status) params.set("status", filters.status);
    if (filters.payment_status) params.set("payment_status", filters.payment_status);
    const { data } = await api.get(`/admin/orders?${params.toString()}`);
    setItems(data.items || []);
    setTotal(data.total || 0);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters]);

  return (
    <div className="space-y-5" data-testid="admin-orders">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <span className="label-overline text-jow-gold">Sales</span>
          <h1 className="font-display text-3xl text-jow-navy mt-1">Orders</h1>
          <p className="text-xs text-jow-navy/55 mt-1">{total} matching</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex-1 min-w-[240px] relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-jow-navy/40" />
          <input
            type="text"
            placeholder="Search by order #, phone, name…"
            value={filters.q}
            onChange={(e) => {
              setFilters((f) => ({ ...f, q: e.target.value }));
              setPage(1);
              setSearchParams(e.target.value ? { q: e.target.value } : {});
            }}
            className="w-full border border-jow-navy/15 pl-9 pr-3 py-2 text-sm bg-white"
            data-testid="orders-search"
          />
        </div>
        <select
          value={filters.status}
          onChange={(e) => {
            setFilters((f) => ({ ...f, status: e.target.value }));
            setPage(1);
          }}
          className="border border-jow-navy/15 px-3 py-2 text-sm bg-white"
          data-testid="orders-status-filter"
        >
          <option value="">Any status</option>
          {Object.keys(STATUS_COLORS).map((s) => (
            <option key={s} value={s}>{s.replaceAll("_", " ")}</option>
          ))}
        </select>
        <select
          value={filters.payment_status}
          onChange={(e) => {
            setFilters((f) => ({ ...f, payment_status: e.target.value }));
            setPage(1);
          }}
          className="border border-jow-navy/15 px-3 py-2 text-sm bg-white"
          data-testid="orders-pay-filter"
        >
          <option value="">Any payment</option>
          <option value="pending">Pending</option>
          <option value="paid">Paid</option>
          <option value="failed">Failed</option>
          <option value="refunded">Refunded</option>
        </select>
      </div>

      <table className="w-full bg-white border border-jow-navy/10 text-sm">
        <thead className="bg-jow-navy/5 text-jow-navy/70 uppercase tracking-widest text-[10px]">
          <tr>
            <th className="text-left px-4 py-3">Order</th>
            <th className="text-left px-4 py-3">Customer</th>
            <th className="text-left px-4 py-3">City</th>
            <th className="text-right px-4 py-3">Total</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3">Payment</th>
            <th className="px-4 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((o) => (
            <tr
              key={o.order_no}
              className="border-t border-jow-navy/5 hover:bg-jow-cream/30"
              data-testid={`orow-${o.order_no}`}
            >
              <td className="px-4 py-3">
                <p className="font-display text-jow-navy">{o.order_no}</p>
                <p className="text-[10px] text-jow-navy/50">
                  {new Date(o.created_at).toLocaleDateString()}
                </p>
              </td>
              <td className="px-4 py-3 text-jow-navy/80">
                {o.address?.name}
                <br />
                <span className="text-[10px] text-jow-navy/50">{o.address?.phone}</span>
              </td>
              <td className="px-4 py-3 text-jow-navy/70">{o.address?.city || "—"}</td>
              <td className="px-4 py-3 text-right font-display">
                ₹{(o.total || 0).toLocaleString("en-IN")}
              </td>
              <td className="px-4 py-3">
                <span
                  className={`px-2 py-0.5 text-[10px] uppercase tracking-widest ${
                    STATUS_COLORS[o.status] || "bg-jow-navy/5 text-jow-navy/60"
                  }`}
                  data-testid={`orow-status-${o.order_no}`}
                >
                  {o.status.replaceAll("_", " ")}
                </span>
              </td>
              <td className="px-4 py-3">
                <span
                  className={`px-2 py-0.5 text-[10px] uppercase tracking-widest ${
                    PAY_COLORS[o.payment?.status] || "bg-jow-navy/5 text-jow-navy/60"
                  }`}
                >
                  {(o.payment?.method || "").toUpperCase()} ·{" "}
                  {o.payment?.status}
                </span>
              </td>
              <td className="px-4 py-3 text-right">
                <Link
                  to={`/admin/orders/${o.order_no}`}
                  className="text-jow-pink"
                  data-testid={`orow-open-${o.order_no}`}
                >
                  <ChevronRight size={14} />
                </Link>
              </td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr>
              <td colSpan={7} className="text-center py-8 text-jow-navy/50">
                No orders match.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="flex items-center justify-between text-xs text-jow-navy/55">
        <span>
          Page {page} of {Math.max(1, Math.ceil(total / 20))}
        </span>
        <div className="flex gap-2">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="btn-secondary-jow text-[10px] disabled:opacity-30"
            data-testid="prev-page"
          >
            Prev
          </button>
          <button
            disabled={page * 20 >= total}
            onClick={() => setPage((p) => p + 1)}
            className="btn-secondary-jow text-[10px] disabled:opacity-30"
            data-testid="next-page"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
